import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type {
  AccountingEntriesShow,
  AccountingEntry,
  MollieInvoicesShow,
  MollieSettlementDetail,
  MollieSettlementsShow,
  MollieTransactionsShow,
  ReconciliationOverview,
  SalesInvoiceBasis,
  SalesInvoicesShow,
} from '../types/models/Reconciliation';
import type {
  ApiResponseAdminReconciliation,
  ReconciliationFieldErrors,
} from '../types/api/ApiResponseAdminReconciliation';
import { errorMessageText, winauditorExportProblemText } from '../app/Admin/Reconciliation/reconciliationLabels';
import {
  WINAUDITOR_OD_CONTENT_TYPE,
  filenameFromContentDisposition,
  winauditorOdFilename,
} from '../app/Admin/Reconciliation/winauditorOd';

// Staff-only reads over docs/api-types/admin-reconciliation.md. Every view is read LIVE from Mollie (and
// Facturier) on each call, within a 25 s request budget, so a read is expensive: each hook fetches only
// while its tab is `active`, and only when its request (month / year / basis) differs from the last one it
// made — or on an explicit refetch (Actualiser). It deliberately does NOT refetch when `apiFetch` changes
// identity on an OIDC silent renew (it is held in a ref, so a later refetch still uses the fresh token).
// Mount behind the `is_ancalagon_staff` gate. JSend at HTTP 200: branch on `body.status`, never `response.ok`.

export interface ReconciliationReadState<T> {
  /** The data of the CURRENT request (never a previous month's). */
  data: T | null;
  /** A request is in flight, or the current request has not been made yet. */
  loading: boolean;
  /** The failure of the current request, in French. Kept next to `data` when a refetch fails. */
  error: string | null;
  /** The backend said the entity does not exist (Show Settlement). */
  notFound: boolean;
  /** When `data` was read (ISO). */
  fetchedAt: string | null;
  refetch: () => Promise<void>;
}

type Data<T> = NonNullable<ApiResponseAdminReconciliation<T>['data']>;

interface ReadResult<T> {
  key: string;
  data: T | null;
  error: string | null;
  notFound: boolean;
  fetchedAt: string | null;
}

const FIELD_KEYS: (keyof ReconciliationFieldErrors)[] = ['month', 'year', 'id', 'basis'];

function describeFailure<T>(body: ApiResponseAdminReconciliation<T> | null, httpStatus: number): string {
  if (body === null) {
    return `Réponse illisible du serveur (HTTP ${httpStatus}).`;
  }
  for (const field of FIELD_KEYS) {
    const detail = body.data?.[field];
    if (typeof detail === 'string') {
      return errorMessageText(detail);
    }
  }
  // A non-staff caller or a route the backend does not have yet: the /admin chokepoint answers a JSend
  // `fail` with `data.msg` and no `message`.
  const msg = typeof body.data?.msg === 'string' ? body.data.msg : null;
  const raw = body.message ?? msg;
  return raw ? errorMessageText(raw) : 'Impossible de charger les données.';
}

function isNotFound<T>(body: ApiResponseAdminReconciliation<T> | null): boolean {
  return body?.message === 'Settlement not found' || typeof body?.data?.id === 'string';
}

export function useReconciliationRead<T>(
  path: string | null,
  active: boolean,
  pick: (data: Data<T>) => T | null,
): ReconciliationReadState<T> {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const pickRef = useRef(pick);
  pickRef.current = pick;
  const [result, setResult] = useState<ReadResult<T> | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const requestedRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(async (key: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    requestedRef.current = key;
    setInFlight(true);
    // A failed refetch keeps the data it was refreshing (same key); a new key never shows the old data.
    const keepPrevious = (prev: ReadResult<T> | null, error: string, notFound: boolean): ReadResult<T> => ({
      key,
      data: prev?.key === key ? prev.data : null,
      fetchedAt: prev?.key === key ? prev.fetchedAt : null,
      error,
      notFound,
    });
    try {
      const response = await apiFetchRef.current(key, { signal: controller.signal });
      const body = (await response.json().catch(() => null)) as ApiResponseAdminReconciliation<T> | null;
      const picked = body?.status === 'success' && body.data ? pickRef.current(body.data) : null;
      if (controller.signal.aborted) {
        return;
      }
      if (picked !== null) {
        setResult({ key, data: picked, error: null, notFound: false, fetchedAt: new Date().toISOString() });
        return;
      }
      const message = describeFailure(body, response.status);
      const notFound = isNotFound(body);
      setResult((prev) => keepPrevious(prev, message, notFound));
    } catch (err) {
      if ((err instanceof DOMException && err.name === 'AbortError') || controller.signal.aborted) {
        return;
      }
      const message = err instanceof Error ? `Erreur réseau : ${err.message}` : 'Erreur réseau.';
      setResult((prev) => keepPrevious(prev, message, false));
    } finally {
      if (abortRef.current === controller) {
        setInFlight(false);
      }
    }
  }, []);

  useEffect(() => {
    if (active && path !== null && auth.isAuthenticated && requestedRef.current !== path) {
      void run(path);
    }
  }, [active, path, auth.isAuthenticated, run]);

  // Unmount: abort, and forget the request so a (StrictMode) remount makes it again.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      requestedRef.current = null;
    },
    [],
  );

  const refetch = useCallback(async () => {
    if (path !== null) {
      await run(path);
    }
  }, [path, run]);

  const current = result?.key === path ? result : null;
  return {
    data: current?.data ?? null,
    loading: inFlight || (path !== null && current === null),
    error: current?.error ?? null,
    notFound: current?.notFound ?? false,
    fetchedAt: current?.fetchedAt ?? null,
    refetch,
  };
}

const withWarnings = <T extends { warnings: unknown[] }>(d: T): T => ({ ...d, warnings: d.warnings ?? [] });

const pickOverview = (d: Data<ReconciliationOverview>): ReconciliationOverview | null =>
  d.period && d.mollie && d.wallet ? withWarnings({ flags: [], ...d } as ReconciliationOverview) : null;

const pickTransactions = (d: Data<MollieTransactionsShow>): MollieTransactionsShow | null =>
  d.period && Array.isArray(d.transactions) && d.totals ? withWarnings(d as MollieTransactionsShow) : null;

const pickSettlements = (d: Data<MollieSettlementsShow>): MollieSettlementsShow | null =>
  Array.isArray(d.settlements) && d.totals ? withWarnings(d as MollieSettlementsShow) : null;

const pickSettlement = (d: Data<MollieSettlementDetail>): MollieSettlementDetail | null =>
  d.settlement && Array.isArray(d.lines) && d.totals && d.checks ? withWarnings(d as MollieSettlementDetail) : null;

const pickInvoices = (d: Data<MollieInvoicesShow>): MollieInvoicesShow | null =>
  Array.isArray(d.invoices) && d.totals ? withWarnings(d as MollieInvoicesShow) : null;

const pickSales = (d: Data<SalesInvoicesShow>): SalesInvoicesShow | null =>
  d.period && Array.isArray(d.invoices) && d.totals ? withWarnings(d as SalesInvoicesShow) : null;

const pickEntries = (d: Data<AccountingEntriesShow>): AccountingEntriesShow | null =>
  d.period && Array.isArray(d.entries) && d.totals && d.check
    ? withWarnings({ accounts: [], customers: [], by_account: [], ...d } as AccountingEntriesShow)
    : null;

const BASE = '/admin/reconciliation';
const qs = (params: Record<string, string>) => new URLSearchParams(params).toString();

/** `GET /admin/reconciliation/overview?month=YYYY-MM` — the month at a glance. */
export const useReconciliationOverview = (month: string, active: boolean) =>
  useReconciliationRead(`${BASE}/overview?${qs({ month })}`, active, pickOverview);

/** `GET /admin/reconciliation/mollie/transactions?month=YYYY-MM` — the Mollie balance journal. */
export const useMollieTransactions = (month: string, active: boolean) =>
  useReconciliationRead(`${BASE}/mollie/transactions?${qs({ month })}`, active, pickTransactions);

/** `GET /admin/reconciliation/mollie/settlements?year=YYYY` — the payouts (bank lines) of a year. */
export const useMollieSettlements = (year: number, active: boolean) =>
  useReconciliationRead(`${BASE}/mollie/settlements?${qs({ year: String(year) })}`, active, pickSettlements);

/** `GET /admin/reconciliation/mollie/settlements/{stl_id}` — one payout broken down. */
export const useMollieSettlement = (id: string | null, active: boolean) =>
  useReconciliationRead(
    id === null ? null : `${BASE}/mollie/settlements/${encodeURIComponent(id)}`,
    active,
    pickSettlement,
  );

/** `GET /admin/reconciliation/mollie/invoices?year=YYYY` — Mollie's own fee invoices. */
export const useMollieInvoices = (year: number, active: boolean) =>
  useReconciliationRead(`${BASE}/mollie/invoices?${qs({ year: String(year) })}`, active, pickInvoices);

/** `GET /admin/reconciliation/sales-invoices?month=YYYY-MM&basis=issued|paid` — the sales journal. */
export const useSalesInvoices = (month: string, basis: SalesInvoiceBasis, active: boolean) =>
  useReconciliationRead(`${BASE}/sales-invoices?${qs({ month, basis })}`, active, pickSales);

/** `GET /admin/reconciliation/accounting-entries?month=YYYY-MM` — the month's entries for WinAuditor. */
export const useAccountingEntries = (month: string, active: boolean) =>
  useReconciliationRead(`${BASE}/accounting-entries?${qs({ month })}`, active, pickEntries);

export type WinAuditorOdExportResult = { ok: true } | { ok: false; message: string };

/**
 * `GET /admin/reconciliation/accounting-entries/winauditor-od?month=&entry=` — downloads ONE OD as the .xlsx
 * WinAuditor imports. Success is the raw file (not JSend); every failure is a JSend `error` at HTTP 200, so
 * the Content-Type is the only discriminator (like the invoice PDF). `exportingId` is the entry in flight.
 */
export function useWinAuditorOdExport(): {
  exportOd: (month: string, entry: AccountingEntry) => Promise<WinAuditorOdExportResult>;
  exportingId: string | null;
} {
  const apiFetch = useApiFetch();
  const [exportingId, setExportingId] = useState<string | null>(null);

  const exportOd = useCallback(
    async (month: string, entry: AccountingEntry): Promise<WinAuditorOdExportResult> => {
      setExportingId(entry.id);
      try {
        const response = await apiFetch(`${BASE}/accounting-entries/winauditor-od?${qs({ month, entry: entry.id })}`);
        const contentType = response.headers.get('Content-Type') ?? '';
        if (contentType.includes(WINAUDITOR_OD_CONTENT_TYPE)) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download =
            filenameFromContentDisposition(response.headers.get('Content-Disposition')) ?? winauditorOdFilename(entry);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { ok: true };
        }
        const body = (await response.json().catch(() => null)) as ApiResponseAdminReconciliation<unknown> | null;
        if (typeof body?.data?.entry === 'string') {
          // « This entry cannot be imported… » carries the reason (e.g. the customers without a code).
          const title = body.message ? errorMessageText(body.message) : null;
          const detail = winauditorExportProblemText(body.data.entry);
          return { ok: false, message: title ? `${title} ${detail}` : detail };
        }
        return { ok: false, message: describeFailure(body, response.status) };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? `Erreur réseau : ${err.message}` : 'Erreur réseau.' };
      } finally {
        setExportingId(null);
      }
    },
    [apiFetch],
  );

  return { exportOd, exportingId };
}
